import { FormStore } from '@roc/feature-app-core';
import { action, makeObservable, observable, flow } from 'mobx';
import { StabilizedBridgeStore } from './stabilizedBridgeStore';
import { GlobalStore } from '@roc/feature-app-core';
import { AttorneysStore } from '../common/attorneysStore';
import CompanyFormStore from '../common/companyFormStore';
import { LoanService } from '../../../loans/services/loanService';
import { BRIDGE, LoanSubType, AllowedWimba, WIMBA, PURCHASE, ELMSURE, OTHER, isNotBlank } from '@roc/feature-utils';
import { LoanOriginatorStore } from '../common/loanOriginatorStore';
import { InsuranceCompanyFormStore } from '../common/insuranceCompanyFormStore';
import { TitleCompanyFormStore } from '../common/titleCompanyFormStore';
import { useStabilizedBridgeStore } from '../../hooks/useStabilizedBridgeStore';
import { constants } from 'buffer';
import { ApiResponse } from '@roc/client-portal-shared/services';
import { FileUpload } from '@roc/ui';
import { getDealName } from '../../utils/dealNameUtils';
import { LoanParticipantsStore } from '@roc/feature-loan-participants';

const loanInformationForm = {
  fields: {
    loanPurpose: {
      value: '',
      error: null,
      rule: 'required',
      message:
        'Loan purpose is required',
    },
    loanAmount: {
      value: 0,
      error: null,
      rule: 'required',
    },
    loanToValue: {
      value: 80,
      error: null,
      rule: 'required',
    },
    rate: {
      value: 12.0,
      error: null,
      rule: 'required',
    },
    pointsFormat: {
      value: 'percentage',
      error: null,
      rule: 'required',
    },
    points: {
      value: 2,
      error: null,
      rule: `required|numeric|min:1|max:6`,
      message:
        `The points field is required. Percentage must be between 1 and 6`,
    },
    pointsNumber: {
      value: 0,
      error: null,
      rule: 'required|numeric',
    },
    brokerPointsFormat: {
      value: 'percentage',
      error: null,
      rule: 'required',
    },
    brokerPoints: {
      value: 0.0,
      error: null,
      rule: 'required|numeric|min:0|max:6',
      message:
        'The broker points field is required. Percentage must be between 0 and 6',
    },
    brokerPointsNumber: {
      value: 0,
      error: null,
      rule: 'required|numeric',
    },
    referralFee: {
      value: 0,
      error: null,
      rule: 'required|numeric|min:0|max:1',
      message:
        'The referral fee field is required and cannot exceed 1% of the total loan amount',
    },
    lenderUnderwritingFee: {
      value: 0,
      error: null,
      rule: 'required',
    },
    lenderProcessingFee: {
      value: 0,
      error: null,
      rule: '',
    },
    externalLenderUnderwritingFee: {
      value: 0,
      error: null,
      rule: '',
    },
    externalLenderProcessingFee: {
      value: 0,
      error: null,
      rule: '',
    },
    rocPointsIn: {
      value: null,
      error: null,
      rule: '',
    },
    interestAccrualMethod: {
      value: 'Full Boat',
      error: null,
      rule: 'required',
    },
    interestReserveMonths: {
      value: '1',
      error: null,
      rule: 'required',
    },
    duration: {
      value: '12',
      error: null,
      rule: 'required',
    },
    participation: {
      value: 'No Participation',
      error: null,
      rule: 'required',
    },
    origLenderSubordination: {
      value: 0.0,
      error: null,
      rule: 'required',
    },
    preferredClosingAttorney: {
      value: 'lawyer.hdrbb@roccapital.com',
      error: null,
      rule: 'required',
    },
    preferredInsurance: {
      value: '',
      error: null,
      rule: 'required',
    },
    loanOriginator: {
      value: null,
      error: null,
      rule: '',
    },
    submitAppraisalWithTamarisk: {
      value: 'Y',
      error: null,
      rule: 'required',
    },
    requestedClosingDate: {
      value: null,
      error: null,
      rule: '',
    },
    amount: {
      value: 0,
      error: null,
      rule: '',
    },
    preferredTitle: {
      value: '',
      error: null,
      rule: '',
    },
    wimbaAcknowledged: {
      value: null,
      error: null,
      rule: '',
    },
    fastTrack: {
      value: 'N',
      error: null,
      rule: 'required',
    },
    fastTrackOptions: {
      value: { insuranceReview: false, titleSearch: false, insurance: false, rushedAppraisal: false, titleAttorneySearch: false },
      error: null,
      rule: ''
    },
    firstTimeBorrowerFreeAppraisal: {
      value: 'N',
      error: null,
      rule: '',
    },
    referralFeesFormat: {
      value: 'percentage',
      error: null,
      rule: 'required',
    },
    referralFeesAmount: {
      value: 0,
      error: null,
      rule: 'required|numeric',
      message: 'The referral fee field is required',
    },
    projectDescription: {
      value: '',
      error: null,
      rule: '',
    },
	marketingPromotionId: {
      value: null,
      error: null,
      rule: '',
    }
  },
  meta: {
    isValid: false,

    error: null,
  },
};

export class LoanInformationStore extends FormStore {
  private globalStore: GlobalStore;
  private StabilizedBridgeStore: StabilizedBridgeStore;
  private attorneysStore: AttorneysStore;
  private totalPurchasePrice = 0;
  private totalRenovationBudget = 0;
  private otherInsuranceDetails = {};
  private otherTitleDetails = {};
  otherInsuranceFormStore: CompanyFormStore;
  otherTitleFormStore: CompanyFormStore;
  loanOriginatorStore: LoanOriginatorStore;
  loanParticipantsStore: LoanParticipantsStore;
  public lenderAttorneysOptionList: any[];
  public wimbaStates: any[];
  public loanService: LoanService;
  public draftLoanId: string;
  public fastTrackFees = {};
  public fastTrackPaymentUrl: string;
  public licenseUpload: FileUpload[];
  public preferredAttorneyForBroker: string;

  constructor(globalStore, StabilizedBridgeStore) {
    super({ ...loanInformationForm }, globalStore);
    this.globalStore = globalStore;
    this.StabilizedBridgeStore = StabilizedBridgeStore;
    this.attorneysStore = new AttorneysStore(this.globalStore);
    this.loanOriginatorStore = new LoanOriginatorStore(this.globalStore);
    this.otherInsuranceFormStore = new InsuranceCompanyFormStore(this.globalStore);
    this.loanParticipantsStore = new LoanParticipantsStore(this.globalStore);
    this.otherTitleFormStore = new TitleCompanyFormStore(this.globalStore);
    this.loanService = new LoanService();
    this.setDefaults();
    makeObservable(this, {
      draftLoanId: observable,
      lenderAttorneysOptionList: observable,
      wimbaStates: observable,
      resetLoanInformation: action,
      makeInitialCalculations: action,
      changeLoanAmount: action,
      changeLoanToValue: action,
      changePointsPercentage: action,
      changePointsAmount: action,
      onChange: action,
      getPreferredAttorney: action,
      setOtherInsuranceCompany: action,
      initForm: action,
      getLenderAttorneys: flow,
      getWimbaStates: flow,
      uploadInsuranceDocument: flow,
      fetchLoanId: flow,
      updateLoanFastrackInfo: flow,
      requestFastTrackPaymentEmail: flow,
      loadFastTrackFees: flow,
      getLoanFastTrackPaymentUrl: flow,
      licenseUpload: observable,
      setLicenseUpload: action,
      preferredAttorneyForBroker: observable,
      resetBrokerPointsBasedOnHud: action,
    });
  }

  setDefaults() {
    this.otherInsuranceDetails = {};
    this.otherTitleDetails = {};
    this.lenderAttorneysOptionList = [];
    this.fastTrackFees = {};
    this.fastTrackPaymentUrl = '';
    this.draftLoanId = '';
    this.licenseUpload = [];
  }
  *getLenderAttorneys() {
    const isBroker = this.globalStore.userFeatures.isBroker;
    if (isBroker) {
      const response = yield this.StabilizedBridgeStore.brokersStore.manageBrokersStore.getBrokerVendorClosingSettingsByBrokerId(this.StabilizedBridgeStore.brokersStore.selectBrokersStore.brokers[0].brokerId);
      this.preferredAttorneyForBroker = response.preferredClosingAttorney;
    }
    yield this.attorneysStore.getLenderAttorneys(BRIDGE, this.getStatesFromProperties(this.StabilizedBridgeStore.properties), this.form?.fields?.loanAmount?.value, LoanSubType.STABILIZED_BRIDGE);
    if (isBroker) {
      this.setDefaultPreferredClosingAttorneyForBroker(this.attorneysStore.lenderAttorneysOptionList, this.preferredAttorneyForBroker);
    } else {
      this.setDefaultPreferredClosingAttorney(this.attorneysStore.lenderAttorneysOptionList)
    }
    this.lenderAttorneysOptionList = this.attorneysStore.lenderAttorneysOptionList;
  }

  setDefaultPreferredClosingAttorney(attorneys) {
    if (isNotBlank(this.form.fields.preferredClosingAttorney.value) && !this.isClosingAttorneyPresentOnList(attorneys, this.form.fields.preferredClosingAttorney.value)) {
      this.isClosingAttorneyPresentOnList(attorneys, 'lawyer.hdrbb@roccapital.com') ?
        this.onFieldChange('preferredClosingAttorney', 'lawyer.hdrbb@roccapital.com') :
        this.onFieldChange('preferredClosingAttorney', 'lawyer.privatelenderlaw@roccapital.com');
    }
  }

  isClosingAttorneyPresentOnList(attorneys, preferredClosingAttorney) {
    return attorneys.some(attorney => attorney.value === preferredClosingAttorney);
  }

  setDefaultPreferredClosingAttorneyForBroker(attorneys, preferredClosingAttorney) {
    if (isNotBlank(this.form.fields.preferredClosingAttorney.value)) {
      console.log(this.isClosingAttorneyPresentOnList(attorneys, preferredClosingAttorney))
      this.isClosingAttorneyPresentOnList(attorneys, preferredClosingAttorney) ?
        this.onFieldChange('preferredClosingAttorney', preferredClosingAttorney) :
        this.onFieldChange('preferredClosingAttorney', 'lawyer.hdrbb@roccapital.com');

    }
  }

  *getWimbaStates() {
    try {
      const response = yield this.loanService.getWimbaStates();
      if (response && response.data && response.data.data) {
        this.wimbaStates = Array.isArray(response.data.data) ? response.data.data : JSON.parse(response.data.data)
      }
      if (this.wimbaStates.length == 0) {
        this.wimbaStates = AllowedWimba;
      }
    } catch (error) {
      console.log(error);
      this.wimbaStates = AllowedWimba;
    }
  }

  getStatesFromProperties(properties) {
    const states = properties
      .filter(property => property.state)
      .map(property => property.state);

    return states.join(',');
  }

  getLoanOriginators() {
    this.loanOriginatorStore.fetchOriginators();
  }
  resetLoanInformation() {
    this.reset();
    this.setDefaults();
    this.form = { ...loanInformationForm };
    this.otherInsuranceFormStore.reset();
    this.otherTitleFormStore.reset();
    const rocPointsIn = this.StabilizedBridgeStore.isMultifamilyBridge ? 1.0 : null;
    this.onChange('rocPointsIn', rocPointsIn);
    this.onChange(
      'submitAppraisalWithTamarisk', this.globalStore.lenderInfo?.tamariskDefaultOption ? 'Y' : 'N'
    );
    this.onChange(
      'loanOriginator',
      this.globalStore.userFeatures.isLenderOriginator
        ? this.StabilizedBridgeStore.userStore.userInformation.userId
        : null
    );
    this.onChange(
      'preferredInsurance',
      this.StabilizedBridgeStore.userStore.allowLoanPricing ? ELMSURE : OTHER
    );
  }

  initForm(data) {
    this.loadForm({
      ...data,
      submitAppraisalWithTamarisk: data.submitAppraisalWithTamarisk ? 'Y' : 'N',
    });
  }

  makeInitialCalculations() {
    this.calculateTotalPurchasePrice();
    this.calculateLoanAmount();
  }

  changeLoanAmount(key, value) {
    this.onFieldChange(key, value);
    this.calculateLoanToValue();
    this.onChange(key, value);
  }

  changeLoanToValue(key, value) {
    this.onFieldChange(key, value);
    this.calculateLoanAmount();
    this.getLenderAttorneys();
    this.onChange(key, value);
  }

  resetLoanPurpose() {
    this.form.fields.loanPurpose.value = '';
  }

  changePointsPercentage(key, value, keyAmount) {
    this.onChange(key, value);
    const amount = this.calculatePointsAmount(value);
    this.onChange(keyAmount, amount);
  }

  changePointsAmount(key, value, keyPercentage) {
    this.onChange(key, value);
    const percentage = this.calculatePointsPercentage(value);
    this.onChange(keyPercentage, percentage);
  }

  onChange = (field, value) => {
    this.onFieldChange(field, value);
    const formValues = this.getFormValues();
    this.StabilizedBridgeStore.setLoanTerms({
      ...formValues,
      participation: formValues['participation'],
      lenderParticipation: this.mapLenderParticipation(
        formValues['participation']
      ),
      sellAs: this.mapSellAs(formValues['participation']),
      submitAppraisalWithTamarisk:
        formValues['submitAppraisalWithTamarisk'] === 'Y',
      interestAccrualMethod: formValues['interestAccrualMethod'],
      fullPrincipalInterest:
        formValues['interestAccrualMethod'] === 'Full Boat',
      otherInsuranceCompany: {
        ...this.otherInsuranceDetails,
      },
      otherTitleCompany: {
        ...this.otherTitleDetails,
      },
    });
  };

  private calculatePointsAmount(percentage) {
    const loanAmount = this.form.fields.amount.value ? this.form.fields.amount.value : this.form.fields.loanAmount.value;
    return loanAmount ? (loanAmount / 100) * percentage : 0;
  }

  private calculatePointsPercentage(amount) {
    const loanAmount = this.form.fields.amount.value ? this.form.fields.amount.value : this.form.fields.loanAmount.value;
    return loanAmount ? (amount / loanAmount) * 100 : 0;
  }

  private calculateLoanAmount() {
    const initialPercentage = this.form.fields.loanToValue.value;
    const totalPurchase = this.totalPurchasePrice || 0;
    let loanAmount = 0;
    if (totalPurchase > 0 && initialPercentage > 0) {
      loanAmount = totalPurchase * (initialPercentage / 100);
    }
    this.form.fields.loanAmount.value = Math.round(loanAmount);
  }

  private calculateLoanToValue() {
    const loanAmount = this.form.fields.loanAmount.value;
    const totalPurchase = this.totalPurchasePrice || 0;
    let loanToValue = 0;
    if (totalPurchase > 0 && loanAmount > 0) {
      loanToValue = (loanAmount * 100) / totalPurchase;
    }
    this.form.fields.loanToValue.value = Math.round(loanToValue);
  }

  private calculateTotalPurchasePrice() {
    const properties = this.StabilizedBridgeStore.properties;
    let total = 0;
    if (properties.length > 0) {
      properties.forEach(value => {
        let acquisitionPrice = isNaN(Number(value.acquisitionPrice)) ? 0 : Number(value.acquisitionPrice);
        let asIsValue = isNaN(Number(value.propertyValuation)) ? 0 : Number(value.propertyValuation);
        if (value.loanPurpose === 'Purchase') {
          let min = Math.min(acquisitionPrice, asIsValue);
          total += min;
        } else {
          total += asIsValue;
        }
      });
    }
    this.totalPurchasePrice = total;
  }

  private mapLenderParticipation(participation) {
    if (participation === 'Through life of loan') {
      return 'Participate through life of loan';
    } else if (participation === 'With subsequent buy-out') {
      return 'Participate with subsequent buy-out';
    } else {
      return 'Broker Loan';
    }
  }

  private mapSellAs(participation) {
    if (participation == 'Through life of loan') {
      return 'A-Note';
    } else {
      return 'Whole Loan';
    }
  }

  getPreferredAttorney() {
    return this.lenderAttorneysOptionList.find(
      attorney =>
        attorney.value === this.form.fields.preferredClosingAttorney.value
    );
  }

  setOtherInsuranceCompany() {
    const values = this.otherInsuranceFormStore.getFormValues();
    this.otherInsuranceDetails = {
      name: values.name,
      contactName: values.contactName,
      phone: values.cellPhone,
      address: values.address,
      email: values.emailAddress,
      state: values.state,
      streetName: values.streetName,
      streetNumber: values.streetNumber,
      zipCode: values.zipcode,
      city: values.city,
    };
    this.onChange(
      'preferredInsurance',
      this.form.fields.preferredInsurance.value
    );
  }

  setOtherTitleCompany() {
    const values = this.otherTitleFormStore.getFormValues();
    this.otherTitleDetails = {
      name: values.name,
      contactName: values.contactName,
      phone: values.cellPhone,
      address: values.address,
      email: values.emailAddress,
      state: values.state,
      streetName: values.streetName,
      streetNumber: values.streetNumber,
      zipCode: values.zipcode,
      city: values.city,
    };
    this.onChange('preferredTitle', this.form.fields.preferredTitle.value);
  }

  showWimba = () => {
    const { allowLoanPricing } = this.StabilizedBridgeStore.userStore;
    return (
      allowLoanPricing &&
      this.StabilizedBridgeStore.properties.some(p =>
        this.wimbaStates.includes(p.state)
      ) &&
      this.StabilizedBridgeStore.loanTerms.preferredTitle === WIMBA
    );
  };

  setDefaultPreferredTitle = () => {
    if (!this.showWimba()) {
      this.onChange('preferredTitle', '');
    } else if (!this.StabilizedBridgeStore.loanTerms.preferredTitle) {
      this.onChange('preferredTitle', '');
    }
  };

  *uploadInsuranceDocument(fileUpload: FileUpload) {
    try {
      const file = fileUpload.file;
      const blob = file.slice(0, file.size, file.type);
      const newFile = new File([blob], fileUpload.name, { type: file.type });
      const apiResponse: ApiResponse = yield this.loanService.uploadInsuranceDocument(
        newFile
      );
      //const documentId = apiResponse.data.data.id; -- use this id
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'File uploaded successfully',
      });
    } catch (error) {
      this.globalStore.notificationStore.showSuccessNotification({
        //message: 'Error uploading file ',
        message: 'File uploaded successfully',
      });
    }
  }
  *fetchLoanId() {
    try {
      if (!this.draftLoanId) {
        const apiResponse: ApiResponse = yield this.loanService.fetchDraftLoanId();
        this.draftLoanId = apiResponse.data.data;
        yield this.getLoanFastTrackPaymentUrl();
      }
    } catch (error) {
      // do nothing
    }
  }
  *updateLoanFastrackInfo(loanId) {
    try {
      const { borrowersRows, properties } = this.StabilizedBridgeStore;
      const data = {
        loanId: loanId,
        randomLoanId: this.draftLoanId,
        ...this.getFormValues().fastTrackOptions,
        properties: properties.length,
        propertiesAddresses: properties.map(property => property.address).join(', '),
        dealName: getDealName(borrowersRows, properties)
      }
      yield this.loanService.updateLoanFastrackInfo(data);
    }
    catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while saving the modal state',
      });
    }
  }
  *requestFastTrackPaymentEmail(fastTrackInfo: any) {
    try {
      yield this.loanService.requestFastTrackPaymentEmail(fastTrackInfo);
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Email sent successfully',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending the email',
      });
    }
  }
  *loadFastTrackFees() {
    try {
      const response = yield this.loanService.getLoanFastTrackFees();
      this.fastTrackFees = response.data.data;
    } catch (err) {
    }
  }

  *getLoanFastTrackPaymentUrl() {
    try {
      const response = yield this.loanService.getLoanFastTrackPaymentUrl(this.draftLoanId);

      this.fastTrackPaymentUrl = response.data.data.url;
    } catch (err) {
    }
  }

  calculateTotalAnnualInsurance() {
    let totalAnnualInsurance = 0;
    let annualInsurance = 0;
    const properties = this.StabilizedBridgeStore.properties;
    if (properties.length > 0) {
      properties.forEach(value => {
        annualInsurance = isNaN(Number(value.annualInsurance))
          ? 0
          : Number(value.annualInsurance);
        totalAnnualInsurance += annualInsurance;
      });
    }
    return totalAnnualInsurance;
  }

  setLicenseUpload(items: FileUpload[]) {
    this.licenseUpload = items;
  }

  resetBrokerPointsBasedOnHud(onTheHud: boolean) {
    if (onTheHud) {
      this.form.fields.referralFee.value = 0.0;
    } else {
      this.form.fields.brokerPoints.value = 0.0;
    }
  }

}

export default LoanInformationStore;
